import type { Plattform } from '../shared/api.js';
import type { Geraeteklasse, Modus } from '../shared/pruefpunkte.js';
import { lies, schreibe } from './speicher.js';

interface UaDaten {
  getHighEntropyValues(hinweise: string[]): Promise<{ model?: string; platformVersion?: string; platform?: string }>;
}

/** Läuft die Seite als installierte App (Homescreen) oder im Browser? */
export function erkenneModus(): Modus {
  const standalone =
    matchMedia('(display-mode: standalone)').matches ||
    matchMedia('(display-mode: fullscreen)').matches ||
    matchMedia('(display-mode: minimal-ui)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standalone ? 'app' : 'browser';
}

export function istIos(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

export function istAndroid(): boolean {
  return /Android/.test(navigator.userAgent);
}

export function erkenneKlasse(): Geraeteklasse {
  if (istIos() || istAndroid()) return 'handy';
  return matchMedia('(pointer: coarse)').matches && !matchMedia('(any-pointer: fine)').matches ? 'handy' : 'pc';
}

function browserName(ua: string): string {
  const treffer: [RegExp, string][] = [
    [/EdgA?\/(\d+)/, 'Edge'],
    [/SamsungBrowser\/(\d+)/, 'Samsung Internet'],
    [/CriOS\/(\d+)/, 'Chrome (iOS)'],
    [/FxiOS\/(\d+)/, 'Firefox (iOS)'],
    [/Firefox\/(\d+)/, 'Firefox'],
    [/Chrome\/(\d+)/, 'Chrome'],
    [/Version\/(\d+(?:\.\d+)?).*Safari/, 'Safari'],
  ];
  for (const [muster, name] of treffer) {
    const t = muster.exec(ua);
    if (t) return `${name} ${t[1] ?? ''}`.trim();
  }
  return 'unbekannt';
}

export async function erkennePlattform(): Promise<Plattform> {
  const ua = navigator.userAgent;
  let system = 'unbekannt';
  let geraet: string | undefined;
  const ios = /OS (\d+)_(\d+)(?:_(\d+))? like Mac OS X/.exec(ua);
  if (ios && /iPhone|iPod/.test(ua)) {
    // Ab iOS 26 bleibt die Systemversion in der Kennung bei 18.6 oder 18.7 stehen; Safari nennt
    // die echte in „Version/“ (am iPhone gleich der iOS-Version). Der installierten App fehlt diese Angabe.
    const safari = /CriOS|FxiOS|EdgiOS/.test(ua) ? undefined : /Version\/(\d+(?:\.\d+)*)/.exec(ua)?.[1];
    const kennung = `${ios[1]}.${ios[2]}${ios[3] ? `.${ios[3]}` : ''}`;
    const eingefroren = Number(ios[1]) === 18 && Number(ios[2]) >= 6;
    system = safari ? `iOS ${safari}` : eingefroren ? `iOS ${kennung} oder neuer (Kennung eingefroren)` : `iOS ${kennung}`;
    geraet = 'iPhone';
  } else if (istIos()) {
    // iPadOS meldet sich wie ein Mac; die Version steht nur bei Safari.
    const version = /Version\/(\d+(?:\.\d+)*)/.exec(ua)?.[1];
    system = version ? `iPadOS ${version}` : 'iPadOS';
    geraet = 'iPad';
  } else if (istAndroid()) {
    system = `Android ${/Android (\d+(?:\.\d+)?)/.exec(ua)?.[1] ?? ''}`.trim();
  } else if (/Windows NT/.test(ua)) {
    system = 'Windows';
  } else if (/Mac OS X/.test(ua)) {
    system = 'macOS';
  } else if (/Linux/.test(ua)) {
    system = 'Linux';
  }

  const uaDaten = (navigator as Navigator & { userAgentData?: UaDaten }).userAgentData;
  if (uaDaten) {
    try {
      const werte = await uaDaten.getHighEntropyValues(['model', 'platformVersion']);
      if (werte.model) geraet = werte.model;
      if (system === 'Windows' && werte.platformVersion) {
        // Windows 11 meldet platformVersion ab 13.
        system = Number(werte.platformVersion.split('.')[0]) >= 13 ? 'Windows 11' : 'Windows 10';
      }
      if (istAndroid() && werte.platformVersion) system = `Android ${werte.platformVersion}`;
    } catch {
      // Hinweise nicht verfügbar; die Angaben aus dem User-Agent genügen.
    }
  }

  return {
    system,
    browser: browserName(ua),
    ...(geraet ? { geraet } : {}),
    bildschirm: `${screen.width}×${screen.height}`,
    ua: ua.slice(0, 600),
  };
}

export interface StartInfo {
  /** Laufende Nummer der App-Starts (nur echte Starts, kein Neuladen) */
  startNr: number;
  /** Neu gestartet (App war geschlossen) statt nur neu geladen */
  frisch: boolean;
  /** Zeitpunkt des vorherigen Starts */
  vorherigerStart: string | null;
  /** Letzte Aktivität vor diesem Start (Sichtbarkeit), für „Tage ohne Nutzung“ */
  zuletztAktivVorher: string | null;
}

const SITZUNG = 'tfm-geraetetest-sitzung';

let aktuellerStart: StartInfo = { startNr: 1, frisch: true, vorherigerStart: null, zuletztAktivVorher: null };

/** Angaben zum laufenden App-Start (nach registriereStart). */
export function startInfo(): StartInfo {
  return aktuellerStart;
}

/**
 * Zählt App-Starts. Ein Neustart (App geschlossen und neu geöffnet) leert den
 * sessionStorage, ein Neuladen nicht.
 */
export async function registriereStart(): Promise<StartInfo> {
  let frisch = true;
  try {
    frisch = sessionStorage.getItem(SITZUNG) === null;
    sessionStorage.setItem(SITZUNG, '1');
  } catch {
    // Ohne sessionStorage zählt jeder Aufruf als Start.
  }
  const bisher = (await lies('starts')) ?? 0;
  const vorherigerStart = (await lies('letzterStart')) ?? null;
  const zuletztAktivVorher = (await lies('zuletztAktiv')) ?? vorherigerStart;
  const startNr = frisch ? bisher + 1 : Math.max(bisher, 1);
  if (frisch) {
    await schreibe('starts', startNr);
    await schreibe('letzterStart', new Date().toISOString());
  }
  await schreibe('zuletztAktiv', new Date().toISOString());
  aktuellerStart = { startNr, frisch, vorherigerStart, zuletztAktivVorher };
  return aktuellerStart;
}
